/**
 * AI capability classes and the reliability copy shown with AI output.
 *
 * The client only ever deals in these capability names — never provider or
 * model ids. The server decides which capability a request gets (plan
 * entitlement + daily allowance + detection below) and which models serve it
 * (server/config/ai-capabilities.ts).
 */

export type AICapability = "standard" | "advanced_reasoning" | "advanced_math";

export const CAPABILITY_LABELS: Record<AICapability, string> = {
  standard: "Standard AI",
  advanced_reasoning: "Advanced Reasoning",
  advanced_math: "Advanced Math",
};

/** Why a request ended up on Standard AI when something stronger was recommended. */
export type CapabilityFallbackReason =
  | "not_entitled" // plan doesn't include it
  | "limit_reached" // today's advanced allowance is used up
  | "unavailable"; // advanced models failed; answered by Standard AI

export interface CapabilityOutcome {
  /** What the request would ideally use. */
  recommended: AICapability;
  /** What actually produced the answer. */
  used: AICapability;
  reason?: CapabilityFallbackReason;
}

/** Bump the version when the acknowledged wording materially changes. */
export const STANDARD_MATH_ACK_KEY = "standard_math_v1";

export const AI_COPY = {
  disclaimer: "AI can make mistakes. Check important information.",
  disclaimerContext:
    "Novari uses AI to help you study. AI can make mistakes, so check important answers and calculations.",
  disclaimerMath:
    "AI can make mistakes, especially on complex calculations. Check important answers and working.",
  grading: "AI feedback is an estimate and may differ from a teacher or official examiner.",
  extraction: "We read these questions from your file. Check them against the original before you study.",
  standardExplainer:
    "Standard AI works well for everyday study help. Complex maths and multi-step reasoning can be more difficult, so answers may contain more mistakes.",
  advancedMathExplainer:
    "Advanced Math uses a stronger reasoning model for complex calculations and multi-step problems.",
  advancedReasoningExplainer:
    "Advanced Reasoning uses a stronger reasoning model for multi-step questions.",
  standardMathWarning: "Complex maths can be harder for Standard AI, so the answer may contain mistakes.",
  standardMathAck:
    "Standard AI may make more mistakes on complex maths and multi-step calculations. Check the answer and working carefully.",
  advancedUnavailable:
    "Advanced Math is temporarily unavailable. You can continue with Standard AI, which may be less reliable for complex calculations.",
  advancedLimitReached:
    "You've used today's Advanced Math allowance, so this answer used Standard AI. Check the working carefully.",
} as const;

export interface MathAssessment {
  /** none: not maths · simple: one-step arithmetic · complex: multi-step / symbolic / quantitative science */
  level: "none" | "simple" | "complex";
  /** Capability that would materially help, or null for Standard. */
  recommended: AICapability | null;
  signals: string[];
}

const STRONG_TERMS: Array<[RegExp, string]> = [
  [/\bsimultaneous\b/i, "simultaneous"],
  [/\bquadratic\b/i, "quadratic"],
  [/\b(differentiate|derivative|dy\/dx|d\/dx|stationary points?|turning points?|gradient of the curve)\b/i, "calculus"],
  [/\b(integrate|integral|integration|area under)\b/i, "calculus"],
  [/\b(log(arithm)?s?|ln)\b\s*[\d(a-z]/i, "logarithm"],
  [/\b(sin|cos|tan|sec|cosec|cot)\s*[\d(θa-z]/i, "trigonometry"],
  [/\b(probability|permutations?|combinations?|binomial|expected value|variance|standard deviation)\b/i, "statistics"],
  [/\b(matri(x|ces)|determinant|vectors?)\b/i, "linear algebra"],
  [/\b(progression|sum to infinity|nth term|series)\b/i, "sequences"],
  [/\b(prove|show that|deduce|derive)\b/i, "proof"],
  [/\b(inequalit(y|ies)|modulus|surds?|indices)\b/i, "algebra"],
  [/\b(velocit(y|ies)|accelerat\w*|speed|distance|displacement|projectile|momentum|resist(ance|ors?)|emf|voltage|current|wavelength|frequency|kinetic energy|potential energy|pressure|density|work done|power)\b/i, "physics calculation"],
  [/\b(moles?|molar|molarity|concentration|titration|neutrali[sz]es?|empirical formula|yield|mass of)\b/i, "chemistry calculation"],
];

const SOLVE_VERBS = /\b(solve|calculate|evaluate|find|determine|work out|simplify|expand|factori[sz]e|compute)\b/i;
const ALGEBRA = /(\b\d*[a-z]\s*[\^²³]|\b\d+[a-z]\b|[a-z]\s*[+\-*/=]\s*\d|\d\s*[+\-*/]\s*[a-z]\b|√|∫|∑|π)/i;
const EQUATION = /[a-z0-9)\]]\s*=\s*[-a-z0-9(]/i;
const PURE_ARITHMETIC = /^[\s\d.,+\-*/×÷^()%=?]+$/;

/**
 * Deterministic "would a stronger reasoning model materially help?" check.
 * Cheap on purpose: no extra model call just to pick a model. It is
 * conservative — simple arithmetic ("What is 4 × 6?") stays on Standard.
 */
export function assessMathComplexity(text: string, subject?: string | null): MathAssessment {
  const t = (text || "").slice(0, 4000);
  const signals: string[] = [];
  let score = 0;

  for (const [re, label] of STRONG_TERMS) {
    if (re.test(t) && !signals.includes(label)) {
      signals.push(label);
      score += 2;
    }
  }
  const hasAlgebra = ALGEBRA.test(t);
  if (hasAlgebra) { signals.push("algebraic expression"); score += 2; }
  if (EQUATION.test(t) && hasAlgebra) { signals.push("equation"); score += 1; }
  const numbers = t.match(/\d+(\.\d+)?/g) || [];
  if (numbers.length >= 3) score += 1;
  const operators = t.match(/[+\-*/×÷^=]/g) || [];
  if (operators.length >= 3) score += 1;
  if (SOLVE_VERBS.test(t)) score += 1;
  if (/\bstep[- ]by[- ]step\b|\bshow (your|all|the) working\b/i.test(t)) score += 1;
  if (subject && /math|physics|chem|statistic|further|elective|account|econ/i.test(subject)) score += 1;

  // One short sum with no variables ("What is 4 x 6?", "12.5 + 3") is simple.
  const stripped = t.replace(/^\s*(what is|what's|calculate|evaluate|compute|find)\s*/i, "").replace(/\?$/, "");
  const isShortArithmetic = PURE_ARITHMETIC.test(stripped) && operators.length <= 2 && numbers.length <= 3;

  const quantitative = numbers.length > 0 || hasAlgebra;
  if (isShortArithmetic) return { level: "simple", recommended: null, signals: ["arithmetic"] };
  if (quantitative && score >= 4) return { level: "complex", recommended: "advanced_math", signals };
  if (signals.includes("proof") && score >= 3) return { level: "complex", recommended: "advanced_math", signals };
  if (quantitative && score >= 2) return { level: "simple", recommended: null, signals };
  return { level: "none", recommended: null, signals };
}
